import React, { useState, useEffect, useRef } from 'react';
import { Bell } from 'lucide-react';

export const NotificationDropdown = () => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);
  
  // Dummy notifications data
  const notifications = [
    {
      id: 1,
      title: 'New message received',
      message: 'You have a new message from John Doe',
      time: '2 min ago',
      read: false
    },
    {
      id: 2,
      title: 'System update',
      message: 'Your system has been updated to version 2.3.1',
      time: '1 hour ago',
      read: true
    },
    {
      id: 3,
      title: 'Payment received',
      message: 'Your invoice #12345 has been paid',
      time: '3 hours ago',
      read: true
    }
  ];

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const unreadCount = notifications.filter(n => !n.read).length;

  return (
    <div className="relative" ref={dropdownRef}>
      <button 
        onClick={() => setIsOpen(!isOpen)}
        className="p-2 rounded-full cursor-pointer text-black hover:text-indigo-800 hover:bg-indigo-50 transition-all duration-200 relative"
        aria-label="Notifications"
        aria-haspopup="true"
        aria-expanded={isOpen}
      >
        <Bell className="h-6 w-6" />
        {unreadCount > 0 && (
          <span className="absolute top-2 right-2 h-2 w-2 rounded-full bg-red-500">
            <span className="sr-only">{unreadCount} unread notifications</span>
          </span>
        )}
      </button>
      
      {isOpen && (
        <div className="absolute -right-5 mt-2 w-100 bg-white rounded-md shadow-lg overflow-hidden z-50 border border-gray-100 transform origin-top-right transition-all duration-100 ease-out">
          <div className="py-1 max-h-96 overflow-y-auto">
            <div className="px-4 py-2 border-b border-gray-100 bg-indigo-50">
              <p className="text-sm font-medium text-indigo-700">Notifications</p>
              {unreadCount > 0 && (
                <span className="ml-2 text-xs font-medium text-indigo-600 bg-indigo-100 px-2 py-0.5 rounded-full">
                  {unreadCount} new
                </span>
              )}
            </div>
            
            {notifications.length > 0 ? (
              notifications.map((notification) => (
                <div 
                  key={notification.id} 
                  className={`px-4 py-3 hover:bg-indigo-50 cursor-pointer transition-colors ${!notification.read ? 'bg-indigo-50' : ''}`}
                  onClick={() => {
                    // Mark as read when clicked
                    notification.read = true;
                    setIsOpen(false);
                  }}
                >
                  <div className="flex items-start">
                    <div className="flex-shrink-0 h-10 w-10 rounded-full bg-indigo-100 flex items-center justify-center">
                      <Bell className="h-4 w-4 text-indigo-500" />
                    </div>
                    <div className="ml-3 flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">{notification.title}</p>
                      <p className="text-sm text-gray-500 truncate">{notification.message}</p>
                      <p className="text-xs text-gray-400 mt-1">{notification.time}</p>
                    </div>
                    {!notification.read && (
                      <div className="ml-2 flex-shrink-0 h-2 w-2 rounded-full bg-indigo-500"></div>
                    )}
                  </div>
                </div>
              ))
            ) : (
              <div className="px-4 py-6 text-center">
                <p className="text-sm text-gray-500">No new notifications</p>
              </div>
            )}
            
            <div className="px-4 py-2 border-t border-gray-100 bg-gray-50 text-center">
              <a 
                href="#" 
                className="text-sm font-medium text-indigo-600 hover:text-indigo-500 inline-flex items-center"
                onClick={(e) => {
                  e.preventDefault();
                  // Handle view all action
                  setIsOpen(false);
                }}
              >
                View all notifications
              </a>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};